import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { fetchDashboard, fetchPrinters, submitPrintJob } from '../lib/printApi';
import { processImageForPreview } from '../utils/processImageForPreview';

const FALLBACK_PRINTERS = [
  { id: 'north-studio-01', name: 'North Studio Printer', status: 'Online', location: 'Brooklyn' },
  { id: 'gallery-07', name: 'Gallery Desk Printer', status: 'Idle', location: 'London' },
  { id: 'pop-up-02', name: 'Pop-up Booth Printer', status: 'Printing queue ready', location: 'Rotating' },
];

function PrintStudio() {
  const { user, signOut } = useAuth();
  const [printers, setPrinters] = useState(FALLBACK_PRINTERS);
  const [printerLoadState, setPrinterLoadState] = useState('Loading printers...');
  const [selectedPrinters, setSelectedPrinters] = useState([]);
  const [notes, setNotes] = useState('');
  const [rotatePhoto, setRotatePhoto] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewSize, setPreviewSize] = useState(null);
  const [previewStatus, setPreviewStatus] = useState('Upload an image to see a processed preview.');
  const [previewError, setPreviewError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobResult, setJobResult] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [dashboardError, setDashboardError] = useState('');

  const printerLookup = useMemo(() => new Map(printers.map((printer) => [printer.id, printer])), [printers]);

  useEffect(() => {
    let mounted = true;

    async function loadPrinters() {
      try {
        const response = await fetchPrinters();
        const list = Array.isArray(response?.printers) && response.printers.length > 0 ? response.printers : FALLBACK_PRINTERS;
        if (!mounted) return;
        setPrinters(list);
        setPrinterLoadState(list.length > 0 ? 'Select one or more printers.' : 'No printers are registered yet.');
      } catch (error) {
        if (!mounted) return;
        setPrinters(FALLBACK_PRINTERS);
        setPrinterLoadState(error?.message ? `Printer service unavailable: ${error.message}` : 'Printer service unavailable.');
      }
    }

    loadPrinters();
    return () => {
      mounted = false;
    };
  }, []);


  useEffect(() => {
    let mounted = true;

    async function loadDashboard() {
      try {
        const response = await fetchDashboard();
        if (!mounted) return;
        setDashboard(response);
        setDashboardError('');
        if (Array.isArray(response?.printers) && response.printers.length > 0) {
          setPrinters(response.printers);
          setPrinterLoadState('Select one or more printers.');
        }
      } catch (error) {
        if (!mounted) return;
        setDashboardError(error?.message || 'Live dashboard unavailable.');
      }
    }

    loadDashboard();
    const intervalId = window.setInterval(loadDashboard, 10000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const togglePrinter = (printerId) => {
    setSelectedPrinters((current) =>
      current.includes(printerId) ? current.filter((id) => id !== printerId) : [...current, printerId]
    );
  };

  const generatePreview = async (chosenFile, shouldRotate = rotatePhoto) => {
    if (!chosenFile) {
      setPreviewStatus('Upload an image to see a processed preview. Text-only jobs do not need a preview.');
      setPreviewUrl('');
      setPreviewSize(null);
      setPreviewError('');
      return;
    }

    setIsProcessing(true);
    setPreviewStatus('Processing preview...');
    setPreviewError('');

    try {
      const processed = await processImageForPreview(chosenFile, 384, { rotate90: shouldRotate });
      setPreviewUrl(processed.previewUrl);
      setPreviewSize({ width: processed.width, height: processed.height });
      setPreviewStatus(shouldRotate ? 'Rotated processed preview ready. Width is based on the photo’s original height.' : 'Processed preview ready.');
    } catch (error) {
      setPreviewError(error?.message || 'Could not generate the preview.');
      setPreviewStatus('Preview unavailable.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFile = async (event) => {
    const chosenFile = event.target.files?.[0] || null;
    setFile(chosenFile);
    setFileName(chosenFile ? chosenFile.name : '');
    setPreviewError('');
    setPreviewUrl('');
    setPreviewSize(null);
    setJobResult(null);

    await generatePreview(chosenFile, rotatePhoto);
  };

  const handleRotateChange = async (event) => {
    const checked = event.target.checked;
    setRotatePhoto(checked);
    setJobResult(null);
    await generatePreview(file, checked);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    setJobResult(null);

    const printableText = notes.trim();

    if (!file && !printableText) {
      setPreviewError('Please upload a photo, enter text to print, or both.');
      return;
    }

    if (selectedPrinters.length === 0) {
      setPreviewError('Please select at least one printer.');
      return;
    }

    setIsSubmitting(true);
    setPreviewError('');

    try {
      const result = await submitPrintJob({
        file,
        printerIds: selectedPrinters,
        printText: notes,
        rotatePhoto,
        submittedBy: user?.email || '',
      });
      setJobResult(result);
      setPreviewStatus('Print job queued successfully.');
    } catch (error) {
      setPreviewError(error?.message || 'Could not submit the print job.');
      setPreviewStatus('Print job failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedPrinterNames = selectedPrinters
    .map((printerId) => printerLookup.get(printerId)?.name)
    .filter(Boolean);

  return (
    <Page>
      <Shell>
        <Topbar>
          <div>
            <Eyebrow>Print Studio</Eyebrow>
            <h1>Upload, preview, and print</h1>
            <p>Signed in as {user?.email}</p>
          </div>
          <Actions>
            <Link to="/">Home</Link>
            <button type="button" onClick={signOut}>Sign out</button>
          </Actions>
        </Topbar>

        <Grid>
          <Panel>
            <h2>1. Upload photo</h2>
            <p>Images are optional. Upload one to print a photo, or leave this blank for a text-only print.</p>
            <label className="upload">
              <span>Select image</span>
              <input type="file" accept="image/*" onChange={handleFile} />
            </label>
            {fileName ? <Meta>Selected: {fileName}</Meta> : <Meta>No image selected. You can still print text only.</Meta>}
            <label className="option-row">
              <input type="checkbox" checked={rotatePhoto} onChange={handleRotateChange} disabled={!file || isProcessing} />
              <span>Rotate photo 90° before printing</span>
            </label>
          </Panel>

          <Panel>
            <h2>2. Preview processed photo</h2>
            <p>This shows the 384 px black-and-white image preview. Text-only jobs skip this preview.</p>
            <PreviewFrame>
              {isProcessing ? (
                <PreviewState>Processing preview…</PreviewState>
              ) : previewUrl ? (
                <PreviewImage src={previewUrl} alt="Processed black and white preview" />
              ) : (
                <PreviewState>{previewStatus}</PreviewState>
              )}
            </PreviewFrame>
            {previewSize ? (
              <Meta>
                {rotatePhoto ? 'Rotated output' : 'Output'}: {previewSize.width}px wide × {previewSize.height}px tall
              </Meta>
            ) : null}
            {previewError ? <ErrorText>{previewError}</ErrorText> : null}
            {jobResult ? <SuccessText>Queued job {jobResult.jobId}.</SuccessText> : null}
          </Panel>

          <Panel>
            <h2>3. Text to print</h2>
            <p>Optional. Add text to print after the image, or leave the image blank to print text only.</p>
            <div className="settings-form">
              <label>
                <textarea
                  rows="4"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional. If no image is uploaded, this text will print by itself."
                />
              </label>
            </div>
          </Panel>

          <Panel>
            <h2>4. Choose printer(s)</h2>
            <p>{printerLoadState}</p>
            <div className="printer-list">
              {printers.map((printer) => {
                const checked = selectedPrinters.includes(printer.id);

                return (
                  <label key={printer.id} className={checked ? 'printer active' : 'printer'}>
                    <div className="printer-row">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => togglePrinter(printer.id)}
                      />
                      <div className="printer-copy">
                        <strong>{printer.name}</strong>
                        <span>{printer.location}</span>
                        <small>{printer.status}</small>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            {selectedPrinterNames.length > 0 ? <Meta>Selected: {selectedPrinterNames.join(', ')}</Meta> : null}
            <button type="button" onClick={handleSubmit} disabled={isSubmitting || isProcessing}>
              {isSubmitting ? 'Sending to printer...' : 'Print'}
            </button>
          </Panel>


          <Panel>
            <h2>5. Live dashboard</h2>
            <p>Printer heartbeat, paper state, and queued jobs from the Raspberry Pi hub.</p>
            {dashboardError ? <ErrorText>{dashboardError}</ErrorText> : null}
            <div className="status-list">
              {printers.map((printer) => (
                <div key={printer.id} className="status-card">
                  <strong>{printer.name}</strong>
                  <span>{printer.status || 'unknown'} · paper: {printer.paperStatus || 'unknown'}</span>
                  <small>{printer.firmwareVersion ? `Firmware ${printer.firmwareVersion}` : printer.id}</small>
                </div>
              ))}
            </div>
            <h3>Queued jobs</h3>
            <div className="status-list">
              {dashboard?.queuedJobs?.length ? (
                dashboard.queuedJobs.slice(0, 5).map((job) => (
                  <div key={job.jobId} className="status-card">
                    <strong>{job.jobId}</strong>
                    <span>{job.status}</span>
                    <small>{job.printerIds?.join(', ')}</small>
                  </div>
                ))
              ) : (
                <Meta>No queued jobs right now.</Meta>
              )}
            </div>
          </Panel>
        </Grid>
      </Shell>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #191220 0%, #120c18 100%);
  color: #f4e8f2;
  padding: 24px;
`;

const Shell = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const Topbar = styled.header`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: end;
  padding: 24px 0 32px;

  h1 {
    margin: 6px 0 8px;
    font-size: clamp(2rem, 4vw, 3.4rem);
  }

  p {
    margin: 0;
    color: rgba(244, 232, 242, 0.75);
  }
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #c98bc1;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.8rem;
  font-weight: 700;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  a,
  button {
    border-radius: 999px;
    border: 1px solid rgba(224, 182, 220, 0.22);
    background: rgba(255, 255, 255, 0.04);
    color: #f4e8f2;
    padding: 10px 14px;
    text-decoration: none;
    cursor: pointer;
    font: inherit;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(224, 182, 220, 0.12);
  border-radius: 24px;
  padding: 22px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.18);

  h2 {
    margin: 0 0 10px;
    font-size: 1.25rem;
  }

  p {
    margin-top: 0;
    color: rgba(244, 232, 242, 0.78);
    line-height: 1.5;
  }

  .upload {
    display: grid;
    gap: 10px;
    margin: 14px 0;
    padding: 16px;
    border: 1px dashed rgba(224, 182, 220, 0.2);
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.02);
  }

  .upload span {
    font-weight: 600;
  }

  .upload input {
    color: #f4e8f2;
  }

  .option-row {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-top: 14px;
    color: rgba(244, 232, 242, 0.82);
  }

  .option-row input {
    width: 18px;
    height: 18px;
  }

  .printer-list {
    display: grid;
    gap: 10px;
  }

  .printer {
    display: block;
    border: 1px solid rgba(224, 182, 220, 0.14);
    border-radius: 18px;
    padding: 14px;
    background: rgba(255, 255, 255, 0.03);
    cursor: pointer;
  }

  .printer.active {
    border-color: rgba(224, 182, 220, 0.45);
    background: rgba(224, 182, 220, 0.08);
  }

  .printer-row {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  .printer-copy {
    display: grid;
    gap: 4px;
  }

  .printer-copy strong {
    font-size: 1rem;
  }

  .printer-copy span,
  .printer-copy small {
    color: rgba(244, 232, 242, 0.72);
  }

  .status-list {
    display: grid;
    gap: 10px;
    margin-bottom: 14px;
  }

  .status-card {
    display: grid;
    gap: 4px;
    border: 1px solid rgba(224, 182, 220, 0.14);
    border-radius: 16px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.03);
  }

  .status-card span,
  .status-card small {
    color: rgba(244, 232, 242, 0.72);
  }

  .settings-form {
    display: grid;
    gap: 16px;
  }

  .settings-form label {
    display: grid;
    gap: 8px;
    font-size: 0.95rem;
  }

  textarea {
    width: 90%;
    resize: vertical;
    border: 1px solid rgba(224, 182, 220, 0.25);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.04);
    color: #fff;
    padding: 14px 16px;
    font-size: 1rem;
    outline: none;
  }

  textarea:focus {
    border-color: #e0b6dc;
    box-shadow: 0 0 0 3px rgba(224, 182, 220, 0.15);
  }

  button {
    border: none;
    border-radius: 14px;
    padding: 14px 18px;
    background: #e0b6dc;
    color: #191220;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.15s ease, opacity 0.15s ease;
  }

  button:hover {
    transform: translateY(-1px);
  }

  button:disabled {
    cursor: wait;
    opacity: 0.7;
  }
`;

const Meta = styled.p`
  margin: 12px 0 0;
  font-size: 0.95rem;
  color: rgba(244, 232, 242, 0.72);
`;

const PreviewFrame = styled.div`
  min-height: 280px;
  border-radius: 18px;
  border: 1px solid rgba(224, 182, 220, 0.16);
  background: rgba(255, 255, 255, 0.03);
  overflow: hidden;
  display: grid;
  place-items: center;
  padding: 12px;
`;

const PreviewState = styled.div`
  padding: 18px;
  text-align: center;
  color: rgba(244, 232, 242, 0.75);
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  image-rendering: pixelated;
  border-radius: 12px;
  background: #fff;
`;

const ErrorText = styled.p`
  margin-top: 12px;
  color: #ffb4c7;
`;

const SuccessText = styled.p`
  margin-top: 12px;
  color: #bce3c7;
`;

export default PrintStudio;
